/* 
  ES6 Class + GSAP | Deferred loading 
  Uses native IntersectionObserver for high-performance scroll tracking
*/

if (!customElements.get("hero-carousel")) {
  class HeroCarousel extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector(".hero-carousel__track-container");
      this.slides = Array.from(this.querySelectorAll(".hero-carousel__slide"));
      this.dots = Array.from(this.querySelectorAll(".hero-carousel__dot"));
      this.prevBtn = this.querySelector(".hero-carousel__button--prev");
      this.nextBtn = this.querySelector(".hero-carousel__button--next");

      this.autoplay = this.dataset.autoplay === "true";
      this.speed = parseInt(this.dataset.speed) || 5000;
      this.autoplayTimer = null;
      this.currentIndex = 0;

      if (!this.track || this.slides.length < 1) return;

      this.init();
      this.initGsapAnimation();
    }

    init() {
      this.bindEvents();
      this.setupIntersectionObserver();
      if (this.autoplay && this.slides.length > 1) {
        this.startAutoplay();
        // Pause on interaction
        this.track.addEventListener(
          "mouseenter",
          this.pauseAutoplay.bind(this),
        );
        this.track.addEventListener(
          "mouseleave",
          this.startAutoplay.bind(this),
        );
        this.track.addEventListener(
          "touchstart",
          this.pauseAutoplay.bind(this),
          { passive: true },
        );
        this.track.addEventListener("touchend", this.startAutoplay.bind(this));
      }
    }

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener("click", () => {
          this.pauseAutoplay();
          this.scrollBySlide(-1);
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener("click", () => {
          this.pauseAutoplay();
          this.scrollBySlide(1);
        });
      }
      this.dots.forEach((dot, index) => {
        dot.addEventListener("click", () => {
          this.pauseAutoplay();
          this.scrollToSlide(index);
        });
      });
    }

    // High performance dot updating native via IntersectionObserver
    setupIntersectionObserver() {
      const options = {
        root: this.track,
        threshold: 0.6, // Trigger when 60% of slide is visible
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const activeIndex = this.slides.indexOf(entry.target);
            this.currentIndex = activeIndex;
            this.updateDots(activeIndex);
          }
        });
      }, options);

      this.slides.forEach((slide) => observer.observe(slide));
    }

    scrollBySlide(direction) {
      const slideWidth = this.slides[0].clientWidth;
      let newIndex = this.currentIndex + direction;

      // Loop around
      if (newIndex >= this.slides.length) newIndex = 0;
      if (newIndex < 0) newIndex = this.slides.length - 1;

      this.scrollToSlide(newIndex);
    }

    scrollToSlide(index) {
      const slideWidth = this.slides[0].clientWidth;
      this.track.scrollTo({ left: slideWidth * index, behavior: "smooth" });
    }

    updateDots(activeIndex) {
      if (!this.dots.length) return;
      this.dots.forEach((dot, index) => {
        dot.classList.toggle("is-active", index === activeIndex);
        dot.setAttribute(
          "aria-selected",
          index === activeIndex ? "true" : "false",
        );
      });
    }

    startAutoplay() {
      if (this.autoplayTimer) clearInterval(this.autoplayTimer);
      this.autoplayTimer = setInterval(() => {
        this.scrollBySlide(1);
      }, this.speed);
    }

    pauseAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    initGsapAnimation() {
      // Respects Standard #9: GSAP + prefers-reduced-motion
      if (
        typeof gsap !== "undefined" &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        gsap.from(this, {
          scrollTrigger: {
            trigger: this,
            start: "top 85%",
          },
          y: 40,
          opacity: 0,
          duration: 0.8,
          ease: "power3.out",
        });
      }
    }
  }

  customElements.define("hero-carousel", HeroCarousel);
}
